import { Badge, Button, Group, Modal, PasswordInput, Select, Stack, Switch, Table, TextInput, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, call, hasPermission } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { ErrorAlert, Loading, formatTs, notifyError, notifyOk } from "../components/common";

const ROLES = ["company_admin", "factory_manager", "maintenance_planner", "technician", "operator", "viewer"] as const;
type RoleName = (typeof ROLES)[number];

export function UsersPage() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const [opened, { open, close }] = useDisclosure();
  const [form, setForm] = useState({ email: "", full_name: "", password: "", role: "operator" as RoleName, factory_id: "" });
  const users = useQuery({ queryKey: ["users"], queryFn: () => call(api.GET("/api/v1/users")) });
  const factories = useQuery({ queryKey: ["factories"], queryFn: () => call(api.GET("/api/v1/factories")) });
  const factoryName = (id: string | null) => (id ? factories.data?.find((f) => f.id === id)?.name ?? "?" : "all factories");
  const canManage = hasPermission(me, "user:manage") && me?.roles.includes("company_admin");

  const create = useMutation({
    mutationFn: () => call(api.POST("/api/v1/users", { body: { ...form, factory_id: form.factory_id || null } })),
    onSuccess: () => { notifyOk("User created"); void qc.invalidateQueries({ queryKey: ["users"] }); close(); },
    onError: notifyError,
  });
  const toggle = useMutation({
    mutationFn: (u: { id: string; is_active: boolean }) =>
      call(api.PATCH("/api/v1/users/{user_id}", { params: { path: { user_id: u.id } }, body: { is_active: u.is_active } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["users"] }),
    onError: notifyError,
  });

  if (users.isLoading) return <Loading />;
  if (users.error) return <ErrorAlert error={users.error} />;
  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Users</Title>
        {canManage && <Button onClick={open}>Add user</Button>}
      </Group>
      <Table.ScrollContainer minWidth={700}>
        <Table striped>
          <Table.Thead><Table.Tr><Table.Th>Name</Table.Th><Table.Th>Email</Table.Th><Table.Th>Roles</Table.Th><Table.Th>Last login</Table.Th><Table.Th>Active</Table.Th></Table.Tr></Table.Thead>
          <Table.Tbody>
            {users.data!.map((u) => (
              <Table.Tr key={u.id}>
                <Table.Td>{u.full_name}</Table.Td>
                <Table.Td>{u.email}</Table.Td>
                <Table.Td>
                  <Group gap={4}>{(u.grants ?? []).map((g) => (
                    <Badge key={`${g.role}${g.factory_id}`} variant="light">{g.role.replace("_", " ")} · {factoryName(g.factory_id ?? null)}</Badge>
                  ))}</Group>
                </Table.Td>
                <Table.Td>{formatTs(u.last_login_at)}</Table.Td>
                <Table.Td>
                  <Switch checked={u.is_active} disabled={!canManage || u.id === me?.id}
                    onChange={(e) => toggle.mutate({ id: u.id, is_active: e.currentTarget.checked })} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Modal opened={opened} onClose={close} title="Add user">
        <Stack>
          <TextInput label="Full name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.currentTarget.value })} />
          <TextInput label="Email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.currentTarget.value })} />
          <PasswordInput label="Initial password" description="12+ characters, upper/lower case and digits" required
            value={form.password} onChange={(e) => setForm({ ...form, password: e.currentTarget.value })} />
          <Select label="Role" data={ROLES.map((r) => ({ value: r, label: r.replace("_", " ") }))} value={form.role}
            onChange={(v) => setForm({ ...form, role: (v as RoleName) ?? "viewer" })} />
          <Select label="Factory scope" clearable placeholder="All factories" disabled={form.role === "company_admin"}
            data={(factories.data ?? []).map((f) => ({ value: f.id, label: f.name }))}
            value={form.factory_id || null} onChange={(v) => setForm({ ...form, factory_id: v ?? "" })} />
          <Button loading={create.isPending} onClick={() => create.mutate()}>Create</Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
